import { FC } from 'react'
import {Card, Typography, Row, Col, Button, Divider, Tag, Flex, Table} from 'antd'
import { CrownOutlined } from '@ant-design/icons'
import {motion, Variants} from 'framer-motion'
import {buyPromoCode30, buyPromoCode365, buyPromoCode90} from "../../../../common/utils/promocode";
import {NavigationPages} from "../../../../common/navigation/navigation-pages";

const { Title, Text } = Typography

const offers = [
    {
        title: 'Премиум на месяц',
        price: 290,
        oldPrice: 350,
        subTitle: '',
        pricePerMonth: '290₽',
        popular: false,
        action: buyPromoCode30,
    },
    {
        title: '3 месяца',
        price: 230,
        oldPrice: 290,
        subTitle: '690₽ за 3 мес',
        pricePerMonth: '230₽ / мес',
        popular: true,
        action: buyPromoCode90,
    },
    {
        title: 'Год',
        price: 150,
        oldPrice: 300,
        subTitle: '1800₽ за год',
        pricePerMonth: '150₽ / мес',
        popular: false,
        action: buyPromoCode365,
    }
]

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.1,
            duration: 0.4,
            ease: [0.42, 0, 0.58, 1], // заменили строку на валидный тип Easing
        }
    })
};

const features = [
    {
        icon: '🔓',
        title: 'Неограниченное количество слов',
        desc: 'Добавляй столько, сколько хочешь. Словарь без границ.'
    },
    {
        icon: '👥',
        title: 'Неограниченное количество сообществ',
        desc: 'Создавай свои тематические группы, приглашай друзей и учись вместе.'
    },
    {
        icon: '📚',
        title: 'Полный доступ к спискам слов',
        desc: 'Подборки по фильмам, песням и ситуациям. Учи по интересам.'
    },
    {
        icon: '🤖',
        title: 'AI-ассистент для обучения',
        desc: 'ИИ создаёт карточки, списки и ведёт диалоги.'
    }
]

const dataSource = [
    {
        key: '1',
        feature: '📂 Количество списков',
        free: 'До 30',
        premium: '🚀 Неограниченно',
    },
    {
        key: '2',
        feature: '🧠 Добавление карточки с ИИ',
        free: '—',
        premium: '✅ Доступно',
    },
    {
        key: '3',
        feature: '📄 Генерация списка с ИИ',
        free: '—',
        premium: '✅ Доступно',
    },
    {
        key: '4',
        feature: '🎙️ Аудио-диалог с ИИ (в приложении)',
        free: '—',
        premium: '✅ Доступно',
    },
    {
        key: '5',
        feature: '👥 Создание сообществ',
        free: '2 сообщества',
        premium: '♾️ Без ограничений',
    },
    {
        key: '6',
        feature: '📚 Готовые списки слов',
        free: '🔹 Только базовые',
        premium: '🎯 Все темы и уровни',
    },
    {
        key: '7',
        feature: '📝 Карточки из любого текста',
        free: '—',
        premium: '✅ Поддерживается',
    },
    {
        key: '8',
        feature: '📩 Добавление через Telegram',
        free: '—',
        premium: '✅ Поддерживается',
    },
    {
        key: '9',
        feature: '🛟 Поддержка пользователей',
        free: '—',
        premium: '✅ Быстрая помощь',
    }
]


const columns = [
    {
        title: '',
        dataIndex: 'feature',
        key: 'feature',
        render: (text: string) => <strong>{text}</strong>,
    },
    {
        title: 'Бесплатно',
        dataIndex: 'free',
        key: 'free',
        render: (text: string) => <span>{text}</span>,
    },
    {
        title: (
            <Tag color="purple" style={{ fontSize: 14, padding: '4px 12px' }}>
                Премиум
            </Tag>
        ),
        dataIndex: 'premium',
        key: 'premium',
        render: (text: string) => <span>{text}</span>,
    },
]


const PremiumPage: FC = () => {
    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '3rem 1rem' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <CrownOutlined style={{ fontSize: 48, color: 'var(--color-premium)' }} />
                <Title>VoxLingua Премиум</Title>
                <Text strong style={{ fontSize: 18 }}>Сделай обучение быстрее, удобнее и умнее.</Text>
                <br />
                <Text type="secondary">Ты уже на шаг впереди — открой максимум возможностей!</Text>
            </div>

            <Row gutter={[24, 24]} justify="center" style={{ marginBottom: 48 }}>
                {features.map((f, index) => (
                    <Col xs={24} sm={12} md={12} lg={6} key={index} style={{ display: 'flex' }}>
                        <motion.div
                            initial={false}
                            custom={index}
                            animate="visible"
                            whileHover={{
                                scale: 1.05,
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                            }}
                            transition={{
                                type: 'spring',
                                stiffness: 200,
                                damping: 15,
                                delay: index * 0.1,
                            }}
                            variants={cardVariants}
                            style={{ width: '100%' }}
                        >
                            <Card bordered={false} style={{ textAlign: 'center', minHeight: 250, transition: 'all 0.3s ease' }}>
                                <div style={{ fontSize: 32 }}>{f.icon}</div>
                                <Title level={5}>{f.title}</Title>
                                <Text type="secondary">{f.desc}</Text>
                            </Card>
                        </motion.div>

                    </Col>
                ))}
            </Row>


            <Divider />

            <Row gutter={[24, 24]} justify="center">
                {offers.map((offer, index) => (
                    <Col xs={24} sm={12} md={8} style={{ display: 'flex' }}>
                        <Card
                            bordered
                            style={{
                                height:250,
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 12
                            }}
                        >
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height:200 }}>
                                <Flex vertical justify="space-between" style={{ flex: 1 }}>
                                    <div>
                                        {offer.popular && <Tag color="green">✅ Выбор 73% пользователей</Tag>}
                                        <Title level={4}>{offer.title}</Title>

                                        <div style={{ marginBottom: 12 }}>
                                            <Title level={3} style={{ margin: 0 }}>{offer.pricePerMonth}</Title>
                                            {offer.oldPrice > offer.price && (
                                                <Text delete type="secondary">{offer.oldPrice}₽</Text>
                                            )}
                                            <br />
                                            {offer.subTitle && <Text type="secondary">{offer.subTitle}</Text>}
                                        </div>
                                    </div>

                                    <Button color="purple" variant="solid"  size="large"  onClick={offer.action}>
                                        Купить
                                    </Button>
                                </Flex>
                            </div>
                        </Card>
                    </Col>

                ))}
            </Row>

            <Divider />

            <div style={{ textAlign: 'center', marginTop: 32 }}>
                <Title level={4}>📈 С Премиумом ты учишь быстрее</Title>
                <Text type="secondary">Открой возможности, которые раньше были недоступны.</Text>
            </div>

            <div style={{ overflowX: 'auto', marginTop: 48 }}>
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    bordered
                    style={{ minWidth: 600 }}
                />
            </div>

            <Divider />

            <div style={{ textAlign: 'center', marginTop: 32 }}>
                <Text type="secondary">
                    🔐 <a href={NavigationPages.PREMIUM_HELP}>Проблемы с активацией?</a>
                    <br />
                    📜 <a href={NavigationPages.AFERTA}>Договор-оферты</a>
                    <br />
                    ✅ Безопасная оплата через защищённые сервисы. Можно отменить в любой момент
                </Text>
            </div>
        </div>
    )
}

export default PremiumPage